/* Khách tiềm năng — logic thuần (không phụ thuộc React) để dùng chung cho bảng và ngăn hồ sơ */
import dayjs from 'dayjs';
import type { ChannelCustomerItem } from '../../../api/ChannelCustomer';
import type { CustomerTagItem } from '../../../api/CustomerTag';
import { CODES } from '../components/Data';
import type { ApptCustomer, ApptRef, ApptSvc, ApptType } from './appt';
import type { LeadPhoto } from './photo';
import type { Quote } from './quote';

export const MIN = 60_000;
export const HOUR = 60 * MIN;
export const DAY = 24 * HOUR;
/** Khách mới từ Trực page phải được gọi trong N phút (cùng ngưỡng với dashboard) */
export const NEW_LEAD_MIN = 15;
/** Không kết nối được → gọi lại sau N giờ, tối đa N lần */
export const RETRY_HOURS = 2;
export const MAX_RETRY = 3;
/** Cuộc gọi đến bị nhỡ phải gọi lại trong N phút (R05) */
export const MISSED_MIN = 15;

export type Mang = 'Newhair' | 'Clinic' | 'PTTM' | 'Partnership';
export const MANGS: Mang[] = ['Newhair', 'Clinic', 'PTTM', 'Partnership'];
export type Kenh = 'Gọi' | 'Zalo' | 'Chỉ nhắn tin';
export const KENHS: Kenh[] = ['Gọi', 'Zalo', 'Chỉ nhắn tin'];

export interface Touch {
  id: string;
  at: number;
  kind: 'call' | 'msg' | 'note' | 'stage' | 'appt';
  /** Gọi đi / gọi đến */
  dir?: 'out' | 'in';
  dur?: number;
  code?: string;
  text?: string;
  by: string;
  /** Dòng tải từ server: không gửi lại */
  server?: boolean;
}
/** booked → confirmed → arrived → done; hoặc noshow / cancelled */
export type ApptSt = 'booked' | 'confirmed' | 'cancelled' | 'arrived' | 'noshow' | 'done';
export type OutcomeKind = 'won' | 'deposit' | 'think' | 'no_need' | 'medical';
export interface Outcome { kind: OutcomeKind; amount?: number; note: string; by: string; at: number }
/** Kết quả tư vấn tại cơ sở → mã S mới; "Cần suy nghĩ" trả khách về Telesales gọi lại */
export const OUTCOMES: Record<OutcomeKind, { label: string; stage: string; color: string }> = {
  won: { label: 'Chốt dịch vụ', stage: 'S7', color: 'green' },
  deposit: { label: 'Đặt cọc', stage: 'S7.1', color: 'cyan' },
  think: { label: 'Cần suy nghĩ — trả về Telesales', stage: 'S4', color: 'gold' },
  no_need: { label: 'Đến không có nhu cầu', stage: 'S6.1', color: 'default' },
  medical: { label: 'Rớt do bệnh lý', stage: 'S6.2', color: 'red' },
};
export interface LeadAppt {
  id: string;
  at: number;
  branch: string;
  mang: Mang;
  service: string;
  note: string;
  status: ApptSt;
  by: string;
  createdAt: number;
  /** Ảnh chụp thông tin khách lúc đặt — để màn Sale cơ sở hiện được khi khách không nằm trong trang đang tải */
  cust?: { name: string; phone?: string | null; code?: string | null };
  consultant?: string;
  outcome?: Outcome;
  type?: ApptType;
  /** Các trường giống màn Tạo lịch hẹn; payload = dữ liệu sẽ gửi POST /api/v1/be/appointments khi nối API */
  branchId?: number;
  services?: ApptSvc[];
  staff?: ApptRef;
  telesale?: ApptRef;
  branchStaff?: ApptRef;
  customer?: ApptCustomer;
  payload?: Record<string, unknown>;
  /** Báo giá mà lịch này đặt theo (dịch vụ lấy từ báo giá) */
  quoteCode?: string;
  /** Lịch đã tạo trên hệ thống (POST /appointments) — có ở danh sách Lịch hẹn */
  serverId?: number;
  serverCode?: string;
  cancelReason?: string;
  /** Dời lịch: lịch cũ trỏ tới lịch mới và ngược lại */
  movedTo?: string;
  movedFrom?: string;
}
export interface ChatMsg { id: string; at: number; from: 'k' | 'n'; text: string; by?: string }
export interface Needs {
  van_de?: string;
  vung?: string;
  thoi_gian?: string;
  da_dieu_tri?: string;
  tien_su?: string;
  nhom_tuoi?: string;
  bac_goi?: string;
  mong_muon?: string;
  tinh_trang?: string;
  loai_da?: string;
  the_trang?: string;
  muc_tieu?: string;
  benh_ly?: string;
  ly_do?: string;
  /** Chi tiết thêm cho lý do, VD "ốm dài, rụng nhiều" */
  ly_do_ct?: string;
  rao_can?: string;
  ngan_sach?: string;
  thoi_diem?: string;
  ghi_chu?: string;
  /** Dịch vụ khách quan tâm — quyết định bộ câu hỏi của phiếu */
  dich_vu?: DichVu;
}
export type NeedKey = Exclude<keyof Needs, 'ly_do_ct' | 'ghi_chu' | 'dich_vu'>;
export type DichVu = 'Cấy tóc' | 'Căng chỉ' | 'Giảm cân' | 'Điều trị da' | 'Phun xăm';
export const DICH_VUS: DichVu[] = ['Cấy tóc', 'Căng chỉ', 'Giảm cân', 'Điều trị da', 'Phun xăm'];
export interface NeedField {
  key: NeedKey;
  label: string;
  /** text: gõ tự do + gợi ý · select: chọn 1 · multi: chọn / gõ nhiều ý · reason: lý do + chi tiết */
  type: 'text' | 'select' | 'multi' | 'reason';
  options?: string[];
  placeholder?: string;
}

/** Các câu hỏi chung cuối phiếu cho mọi mảng */
export const NEED_TAIL: NeedField[] = [
  { key: 'ly_do', label: 'Lý do làm lúc này', type: 'reason' },
  { key: 'rao_can', label: 'Rào cản', type: 'multi', options: ['Chi phí', 'Sợ đau', 'Sợ để lại sẹo', 'Thời gian nghỉ', 'Khoảng cách', 'Chưa tin kết quả', 'Cần hỏi người nhà', 'Công việc bận'] },
  { key: 'ngan_sach', label: 'Ngân sách khách dự kiến', type: 'text', options: ['Dưới 5.000.000 đ', '5.000.000 – 10.000.000 đ', '10.000.000 – 20.000.000 đ', '20.000.000 – 40.000.000 đ', 'Trên 40.000.000 đ', 'Khách không chia sẻ'] },
  { key: 'thoi_diem', label: 'Thời điểm muốn làm', type: 'text', options: ['Càng sớm càng tốt', 'Trong tháng này', 'Tháng sau', 'Trước Tết', 'Chưa xác định'] },
];

/** Phiếu nhu cầu theo dịch vụ — trường cố định (theo kịch bản Telesales) */
export const DV_FIELDS: Record<DichVu, NeedField[]> = {
  'Cấy tóc': [
    { key: 'vung', label: 'Vùng rụng', type: 'multi', options: ['Vùng trán', 'Hai bên thái dương', 'Đỉnh đầu', 'Thưa đường ngôi giữa', 'Thưa toàn đầu'] },
    { key: 'thoi_gian', label: 'Thời gian rụng', type: 'text', options: ['Dưới 6 tháng', 'Khoảng 1 năm', 'Khoảng 2 năm', 'Từ 3 năm trở lên'] },
    { key: 'da_dieu_tri', label: 'Đã điều trị', type: 'text', options: ['Chưa', 'Thuốc thoa', 'Uống viên bổ tóc', 'Từng cấy tóc nơi khác'], placeholder: 'VD: thuốc thoa 1 năm, ngưng thì rụng lại' },
    { key: 'tien_su', label: 'Tiền sử gia đình', type: 'text', options: ['Không', 'Không rõ', 'Bố hói', 'Mẹ tóc thưa'] },
    ...NEED_TAIL,
  ],
  'Căng chỉ': [
    { key: 'vung', label: 'Vùng muốn căng', type: 'multi', options: ['Toàn mặt', 'Má · rãnh cười', 'Cằm · V-line', 'Cổ', 'Mắt · chân mày'] },
    { key: 'tinh_trang', label: 'Tình trạng da', type: 'multi', options: ['Chảy xệ nhẹ', 'Chảy xệ nhiều', 'Nếp nhăn', 'Mặt bánh bao', 'Da mỏng'] },
    { key: 'nhom_tuoi', label: 'Nhóm tuổi', type: 'select', options: ['Dưới 30', '30–40', '40–50', 'Trên 50'] },
    { key: 'da_dieu_tri', label: 'Đã làm trước', type: 'text', options: ['Chưa', 'Đã căng chỉ', 'Tiêm filler / botox', 'Nâng cơ HIFU'], placeholder: 'VD: căng chỉ 2 năm trước, nay chảy lại' },
    { key: 'mong_muon', label: 'Mong muốn', type: 'text', placeholder: 'VD: gọn mặt, hết chảy xệ hai bên má' },
    ...NEED_TAIL,
  ],
  'Giảm cân': [
    { key: 'the_trang', label: 'Chiều cao · cân nặng', type: 'text', placeholder: 'VD: 1m60 · 65kg' },
    { key: 'muc_tieu', label: 'Muốn giảm', type: 'text', options: ['Dưới 5kg', '5 – 10kg', '10 – 20kg', 'Trên 20kg', 'Chỉ giảm mỡ vùng'] },
    { key: 'vung', label: 'Vùng muốn giảm', type: 'multi', options: ['Bụng', 'Đùi', 'Bắp tay', 'Lưng', 'Nọng cằm', 'Toàn thân'] },
    { key: 'da_dieu_tri', label: 'Đã thử', type: 'text', options: ['Chưa', 'Ăn kiêng', 'Tập gym', 'Thuốc giảm cân', 'Hút mỡ'], placeholder: 'VD: ăn kiêng 3 tháng, giảm rồi tăng lại' },
    { key: 'benh_ly', label: 'Sức khoẻ · bệnh nền', type: 'text', options: ['Bình thường', 'Tiểu đường', 'Huyết áp', 'Tuyến giáp', 'Đang mang thai / cho con bú'] },
    ...NEED_TAIL,
  ],
  'Điều trị da': [
    { key: 'van_de', label: 'Vấn đề da', type: 'multi', options: ['Mụn', 'Thâm mụn', 'Sẹo rỗ', 'Nám · tàn nhang', 'Lỗ chân lông to', 'Lão hoá · nếp nhăn', 'Da xỉn màu'] },
    { key: 'vung', label: 'Vùng', type: 'multi', options: ['Mặt', 'Lưng', 'Ngực', 'Cổ', 'Toàn thân'] },
    { key: 'loai_da', label: 'Loại da', type: 'select', options: ['Da dầu', 'Da khô', 'Da hỗn hợp', 'Da nhạy cảm', 'Chưa rõ'] },
    { key: 'thoi_gian', label: 'Thời gian bị', type: 'text', options: ['Dưới 6 tháng', 'Khoảng 1 năm', 'Từ 2 năm trở lên'] },
    { key: 'da_dieu_tri', label: 'Đã điều trị', type: 'text', options: ['Chưa', 'Tự dùng mỹ phẩm', 'Đi spa', 'Bác sĩ da liễu'], placeholder: 'VD: từng dùng kem trộn, đang dùng retinol' },
    ...NEED_TAIL,
  ],
  'Phun xăm': [
    { key: 'vung', label: 'Vùng phun xăm', type: 'multi', options: ['Mày', 'Môi', 'Mí mắt', 'Chân tóc'] },
    { key: 'da_dieu_tri', label: 'Đã phun xăm trước', type: 'text', options: ['Chưa', 'Có — muốn dặm lại', 'Có — muốn sửa / xoá'], placeholder: 'VD: phun mày 2023, nay bị xanh' },
    { key: 'mong_muon', label: 'Kiểu · màu mong muốn', type: 'text', placeholder: 'VD: mày ombre nâu, môi hồng cam' },
    { key: 'benh_ly', label: 'Cơ địa', type: 'text', options: ['Bình thường', 'Dễ sẹo lồi', 'Dị ứng thuốc tê', 'Đang mang thai / cho con bú'] },
    ...NEED_TAIL,
  ],
};

/** Phiếu theo mảng khi chưa chọn dịch vụ cụ thể */
export const NEED_FIELDS: Record<Mang, NeedField[]> = {
  Newhair: DV_FIELDS['Cấy tóc'],
  Clinic: [
    { key: 'van_de', label: 'Vấn đề', type: 'text', placeholder: 'VD: da chảy xệ nhẹ vùng má' },
    { key: 'vung', label: 'Vùng', type: 'multi', options: ['Mặt', 'Cổ', 'Da đầu', 'Toàn thân'] },
    { key: 'da_dieu_tri', label: 'Đã làm trước', type: 'text', options: ['Chưa'], placeholder: 'VD: phác đồ K·Reskin 12/2025' },
    { key: 'mong_muon', label: 'Mong muốn', type: 'text', placeholder: 'VD: da săn hơn trước Tết' },
    ...NEED_TAIL,
  ],
  PTTM: [
    { key: 'vung', label: 'Vùng quan tâm', type: 'multi', options: ['Mắt', 'Mũi', 'Cằm', 'Môi', 'Ngực', 'Bụng', 'Mặt'] },
    { key: 'nhom_tuoi', label: 'Nhóm tuổi', type: 'select', options: ['Dưới 25', '25–45', 'Trên 45'] },
    { key: 'bac_goi', label: 'Bậc gói khách hỏi', type: 'select', options: ['Basic', 'Transformation', 'Perfect', 'Chưa hỏi'] },
    { key: 'da_dieu_tri', label: 'Đã phẫu thuật trước', type: 'text', options: ['Chưa'] },
    { key: 'mong_muon', label: 'Mong muốn', type: 'text', placeholder: 'VD: dáng mũi cao, tự nhiên' },
    ...NEED_TAIL,
  ],
  Partnership: [
    { key: 'van_de', label: 'Nhu cầu hợp tác', type: 'text', placeholder: 'VD: mở điểm nhượng quyền tại Đà Nẵng' },
    ...NEED_TAIL,
  ],
};

/** Giá trị hiển thị của 1 trường (lý do = lý do — chi tiết) */
export const needValue = (f: NeedField, n: Needs): string =>
  f.type === 'reason' ? [n.ly_do, n.ly_do_ct?.trim()].filter(Boolean).join(' — ') : (n[f.key] || '').trim();

/** Dịch vụ mặc định suy từ mảng (Newhair chỉ có cấy tóc) */
export const dichVuOf = (mang?: Mang, n?: Needs): DichVu | undefined => n?.dich_vu ?? (mang === 'Newhair' ? 'Cấy tóc' : undefined);

/** Trường của phiếu nhu cầu: ưu tiên theo dịch vụ, rồi theo mảng; chưa có gì thì chỉ có phần hỏi chung */
export const needFieldsOf = (mang?: Mang, dv?: DichVu): NeedField[] => (dv ? DV_FIELDS[dv] : mang ? NEED_FIELDS[mang] : NEED_TAIL);
export interface ChatTag { id: number; name: string; color?: string | null }
/** Dữ liệu Telesales lưu tạm trên máy cho một khách — chờ nối API */
export interface LeadLocal {
  /** Mã S Telesales đã đổi (id thẻ), ưu tiên hơn customer_tag từ API */
  tagId?: number;
  /** Thẻ chat đã gắn / bỏ trên máy, ưu tiên hơn care_statuses từ API */
  chatTags?: ChatTag[];
  mang?: Mang;
  /** Nhóm chia số chọn tay (khi dịch vụ quan tâm chưa đủ để tự nhận) */
  group?: import('./groups').GroupKey;
  /** Ghi chú nhanh của Trực page lúc tạo data (copy câu khách nhắn) — ghim đầu Lịch sử chạm */
  pageNote?: { text: string; by: string; at: number };
  kenh?: Kenh;
  needs?: Needs;
  callback?: number;
  /** Người vận hành phân công lại Telesales (chờ nối API) */
  assign?: { id: number; name: string };
  /** Thời điểm giao cho Telesales hiện tại (mốc tính thời gian không tương tác) */
  assignAt?: number;
  /** Số lần đã chia lại vì không tương tác */
  reassignCount?: number;
  /** Trực page đang phụ trách chat sau khi chia lại (ưu tiên hơn in_charge_staff) */
  chatAssign?: { id: number; name: string };
  chatAssignAt?: number;
  chatReassignCount?: number;
  touches: Touch[];
  appts: LeadAppt[];
  msgs: ChatMsg[];
  /** Báo giá tham khảo + yêu cầu cọc (mới nhất trước) */
  quotes?: Quote[];
  /** Ảnh khách hàng + ảnh quá trình làm dịch vụ (mới thêm trước) */
  photos?: LeadPhoto[];
  /** Ảnh chân dung dùng làm ảnh đại diện (id ảnh) */
  avatar?: string;
}
export const EMPTY_LOCAL: LeadLocal = { touches: [], appts: [], msgs: [] };

/* ---------------- Mã S ---------------- */

/** Phần mã ở đầu tên thẻ: "S5.1 - Khách đặt lịch chưa tới" → "S5.1" */
export const shortTag = (name?: string | null) => String(name || '').split(/\s*-\s*/)[0].trim();

/**
 * Thẻ thuộc giai đoạn khách tiềm năng (Mới, KNC, S0 → S6.x).
 * S7 / S8 / S.8 là khách đã làm dịch vụ, S4.1 là khách chuyển CSKH.
 */
export const isLeadTag = (name?: string | null) => {
  const s = shortTag(name).replace(/\s/g, '');
  if (!s) return true;
  return !/^S\.?[78]/i.test(s) && !/^S4\.1$/i.test(s);
};

export const findTag = (tags: CustomerTagItem[], code: string) =>
  tags.find((t) => shortTag(t.name).toUpperCase() === code.toUpperCase());

/**
 * Thẻ đang hiệu lực của khách: đổi trên máy (sau cuộc gọi) → thẻ từ API.
 * Hệ thống đã chuyển khách ra khỏi giai đoạn tiềm năng (check-in S7, thanh toán S8, CSKH S4.1) thì theo hệ thống.
 */
export const tagOf = (r: ChannelCustomerItem, l: LeadLocal | undefined, tags: CustomerTagItem[]) => {
  if (r.customer_tag && !isLeadTag(r.customer_tag.name)) return { ...r.customer_tag, local: false };
  if (l?.tagId) {
    const t = tags.find((x) => x.id === l.tagId);
    if (t) return { id: t.id, name: t.name, color: t.color, local: true };
  }
  return r.customer_tag ? { ...r.customer_tag, local: false } : null;
};

/* ---------------- Mã kết quả cuộc gọi ---------------- */

export const ALL_CODES = CODES.flatMap((g) => g.items);
/** Mã của cuộc gọi đến không ai nghe — tổng đài tự ghi, không có trong phiếu kết quả */
export const MISSED = 'NHỠ';
export const codeLabel = (code?: string) => (code === MISSED ? 'Cuộc gọi nhỡ' : ALL_CODES.find((c) => c.code === code)?.label.replace(/\s*\(R\d+\)/, '') ?? '');
export const isConnected = (code?: string) => !!code && code.startsWith('KN');

/** Mã S gợi ý sau khi lưu kết quả cuộc gọi; null = giữ nguyên */
export const suggestStage = (code: string, current: string): string | null => {
  const cur = current.toUpperCase();
  switch (code) {
    case 'KN-01': return 'S5';
    case 'KN-02': return ['', 'MỚI', 'S0', 'S1'].includes(cur) ? 'S2' : null;
    case 'KN-03':
    case 'KN-04': return 'S4';
    case 'KN-05':
    case 'KN-06': return 'S3';
    case 'KN-07': return 'S6.2';
    case 'KN-08':
    case 'KN-09':
    case 'KK-04': return 'KNC';
    default: return ['', 'MỚI'].includes(cur) ? 'S0' : null;
  }
};

/* ---------------- Hiển thị ---------------- */

export const maskPhone = (phone?: string | null) => {
  if (!phone) return '';
  return phone.length < 8 ? phone : `${phone.slice(0, 3)}***${phone.slice(-4)}`;
};

export const xungHo = (gender?: number | null) => (gender === 1 ? 'anh' : gender === 0 ? 'chị' : 'anh/chị');

const SOURCE_LABELS: Record<string, string> = {
  facebook: 'Facebook', fb: 'Facebook', zalo: 'Zalo', tiktok: 'TikTok',
  insta: 'Instagram', instagram: 'Instagram', manual: 'Nhập tay', website: 'Website', hotline: 'Hotline',
};
/** Nguồn khách: kênh + trang (Fanpage) nếu có */
export const sourceOf = (r: ChannelCustomerItem) => {
  const conn = r.connections?.find((c) => c.channel_connection || c.source);
  const raw = (r.source || conn?.source || conn?.channel_connection?.channel_type_code || '').toLowerCase();
  const kenh = SOURCE_LABELS[raw] || (raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : '');
  const page = conn?.channel_connection?.display_name || r.connection?.display_name || '';
  return [kenh, page].filter(Boolean).join(' · ');
};

/** Kênh chat có phòng chat trong CRM (khách tạo từ form / nhập tay không có) */
const CHAT_KINDS: Record<string, string> = {
  facebook: 'Facebook', fb: 'Facebook', zalo: 'Zalo', zalo_oa: 'Zalo', tiktok: 'TikTok', insta: 'Instagram', instagram: 'Instagram',
};
export interface ChatLink { key: string; label: string; page: string; url?: string }
/** Nút mở phòng chat của khách: mỗi kênh chat đã liên kết một nút; url trống = chưa có phòng chat để mở */
export const chatLinksOf = (r: ChannelCustomerItem): ChatLink[] => {
  const out: ChatLink[] = [];
  (r.connections || []).forEach((c) => {
    const label = CHAT_KINDS[(c.channel_connection?.channel_type_code || c.source || '').toLowerCase()];
    if (!label) return;
    const connId = c.channel_customer?.channel_connection_id ?? c.channel_connection?.id;
    const url = connId && c.channel_customers_id ? `/chat/${connId}?customer_id=${c.channel_customers_id}` : undefined;
    const page = c.channel_connection?.display_name || '';
    const same = out.find((x) => x.label === label && x.page === page);
    if (same) { same.url = same.url || url; return; }
    out.push({ key: `${c.id}`, label, page, url });
  });
  // Có nguồn chat nhưng API danh sách chưa trả liên kết → vẫn hiện nút (chưa mở được)
  const src = CHAT_KINDS[(r.source || '').toLowerCase()];
  if (!out.length && src) out.push({ key: 'src', label: src, page: r.connection?.display_name || '' });
  return out;
};

/** Đoán mảng theo tên trang nguồn (Fanpage Newhair, Fanpage PTTM...) khi Telesales chưa chọn */
export const mangOf = (r: ChannelCustomerItem, l?: LeadLocal): Mang | undefined => {
  if (l?.mang) return l.mang;
  if (r.mang) return r.mang as Mang;
  const text = `${sourceOf(r)} ${r.note || ''}`.toLowerCase();
  return MANGS.find((m) => text.includes(m.toLowerCase()));
};

export const dur = (ms: number) => {
  const m = Math.round(Math.abs(ms) / MIN);
  if (m < 60) return `${m} phút`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} giờ${m % 60 ? ` ${m % 60} phút` : ''}`;
  return `${Math.floor(h / 24)} ngày`;
};
export const mmss = (sec: number) => `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`;
export const when = (t: number, now: number) => {
  const d = dayjs(t);
  if (d.isSame(now, 'day')) return `Hôm nay ${d.format('HH:mm')}`;
  if (d.isSame(dayjs(now).add(1, 'day'), 'day')) return `Mai ${d.format('HH:mm')}`;
  if (d.isSame(dayjs(now).subtract(1, 'day'), 'day')) return `Hôm qua ${d.format('HH:mm')}`;
  return d.format('DD/MM HH:mm');
};

/* ---------------- Việc tiếp theo ---------------- */

export interface NextStep {
  kind: 'missed' | 'callback' | 'confirm' | 'appt' | 'new' | 'retry' | 'none';
  label: string;
  due?: number;
}

export const lastCall = (l?: LeadLocal) => l?.touches.find((t) => t.kind === 'call');

/* Máy này có thể chưa có lịch sử của khách (đổi máy): lấy thêm tóm tắt server trả ở danh sách, bản nào mới hơn thì dùng */

/** Cuộc gọi gần nhất */
export const lastCallOf = (r: ChannelCustomerItem, l?: LeadLocal): Pick<Touch, 'at' | 'code' | 'dir'> | undefined => {
  const local = lastCall(l);
  const sv = r.last_call;
  if (!sv) return local;
  const at = dayjs(sv.at).valueOf();
  return local && local.at >= at ? local : { at, code: sv.code ?? undefined, dir: sv.direction };
};
/** Lần chạm gần nhất */
export const lastTouchOf = (r: ChannelCustomerItem, l?: LeadLocal): Pick<Touch, 'at' | 'code'> | undefined => {
  const local = l?.touches[0];
  const sv = r.last_touch;
  if (!sv) return local;
  const at = dayjs(sv.at).valueOf();
  return local && local.at >= at ? local : { at, code: sv.code ?? undefined };
};
/** Số cuộc gọi đi không nói chuyện được từ sau lần nói chuyện gần nhất: số của server + các cuộc mới hơn trên máy */
export const missesSinceConnected = (r: ChannelCustomerItem, l?: LeadLocal) => {
  const svAt = r.last_call ? dayjs(r.last_call.at).valueOf() : 0;
  const after = (l?.touches ?? []).filter((t) => t.kind === 'call' && t.dir === 'out' && t.at > svAt);
  const i = after.findIndex((t) => isConnected(t.code));
  return i >= 0 ? i : (r.missed_since_connected ?? 0) + after.length;
};

/** Dòng lịch sử chạm server trả về */
export interface ServerTouch { id: number; client_id?: string | null; kind: Touch['kind']; direction?: 'in' | 'out' | null; duration?: number | null; code?: string | null; text?: string | null; at: string; by?: string | null }
export const touchFromServer = (t: ServerTouch): Touch => ({
  id: t.client_id || `srv-${t.id}`,
  at: dayjs(t.at).valueOf(),
  kind: t.kind,
  ...(t.direction ? { dir: t.direction } : {}),
  ...(t.duration != null ? { dur: t.duration } : {}),
  ...(t.code ? { code: t.code } : {}),
  ...(t.text ? { text: t.text } : {}),
  by: t.by || '',
  server: true,
});
/** Gộp theo id, server thắng; dòng chỉ có trên máy (chưa gửi, hoặc ghi trước khi có API) vẫn giữ */
export const mergeTouches = (local: Touch[], server: ServerTouch[]): Touch[] => {
  const map = new Map(local.map((t) => [t.id, t]));
  server.forEach((s) => { const t = touchFromServer(s); map.set(t.id, t); });
  return Array.from(map.values()).sort((a, b) => b.at - a.at);
};
export const activeAppt = (l: LeadLocal | undefined, now: number) =>
  l?.appts
    .filter((a) => (a.status === 'booked' || a.status === 'confirmed') && a.at >= now - 2 * HOUR)
    .sort((a, b) => a.at - b.at)[0];

/** Hạn xác nhận lịch: lịch trước 12h → 18:00 hôm trước; còn lại → 2 giờ trước giờ hẹn */
export const confirmDue = (at: number, bookedAt?: number) => {
  const d = dayjs(at);
  const due = d.hour() < 12 ? d.subtract(1, 'day').hour(18).minute(0).second(0).valueOf() : at - 2 * HOUR;
  // Lịch đặt sát giờ (mốc xác nhận đã qua lúc đặt): gọi xác nhận trong 30 phút sau khi đặt, không muộn hơn giờ hẹn — không báo "quá hạn" ngay khi vừa đặt
  return bookedAt != null && due < bookedAt ? Math.min(at, bookedAt + 30 * MIN) : due;
};

export function nextStep(r: ChannelCustomerItem, l: LeadLocal | undefined, now: number, tagName?: string | null): NextStep {
  // Khách gọi đến bị nhỡ mà chưa gọi lại → gấp nhất
  const last = lastCallOf(r, l);
  if (last?.code === MISSED && (!tagName || isLeadTag(tagName))) return { kind: 'missed', label: 'Gọi lại cuộc nhỡ', due: last.at + MISSED_MIN * MIN };
  if (l?.callback) return { kind: 'callback', label: 'Gọi lại theo hẹn', due: l.callback };
  // Đã chốt / làm dịch vụ (S7, S8) hoặc chuyển CSKH → hết việc Telesales
  if (tagName && !isLeadTag(tagName)) return { kind: 'none', label: '' };
  const appt = activeAppt(l, now);
  if (appt) {
    return appt.status === 'booked'
      ? { kind: 'confirm', label: `Xác nhận lịch ${dayjs(appt.at).format('DD/MM HH:mm')}`, due: confirmDue(appt.at, appt.createdAt) }
      : { kind: 'appt', label: `Chờ khách đến · ${appt.branch}`, due: appt.at };
  }
  const calls = (l?.touches ?? []).filter((t) => t.kind === 'call' && t.dir === 'out');
  const code = shortTag(tagName).toUpperCase();
  if (!calls.length && !r.out_call_count && ['', 'MỚI', 'S0'].includes(code)) {
    const created = r.created_at ? dayjs(r.created_at).valueOf() : now;
    return { kind: 'new', label: 'Gọi khách mới', due: created + NEW_LEAD_MIN * MIN };
  }
  // Khách đã đến cơ sở sau lần gọi hụt cuối → không còn việc gọi lại
  const visitedAfter = l?.appts.some((a) => (a.status === 'arrived' || a.status === 'done') && last && a.at > last.at);
  if (last && !visitedAfter && !isConnected(last.code) && last.code !== 'KK-04' && last.code !== 'KK-05') {
    const n = missesSinceConnected(r, l);
    if (n < MAX_RETRY) return { kind: 'retry', label: `Gọi lại lần ${n + 1}`, due: last.at + RETRY_HOURS * HOUR };
  }
  return { kind: 'none', label: '' };
}

/* ---------------- Các bước làm với một khách ---------------- */

export type WorkStepKey = 'needs' | 'call' | 'quote' | 'appt' | 'confirm';
/** optional = chỉ làm khi cần (khách hỏi giá), không bao giờ là chỗ "bắt đầu ở đây" */
export interface WorkStep { key: WorkStepKey; label: string; done: boolean; hint: string; optional?: boolean }

/** Nhu cầu → Gọi & lưu phiếu → (Báo giá & cọc) → Đặt lịch → Xác nhận lịch; bước bắt buộc đầu tiên chưa xong là chỗ bắt đầu */
export function workSteps(l: LeadLocal | undefined, mang: string | undefined, tagName?: string | null): WorkStep[] {
  const touches = l?.touches ?? [];
  const appts = (l?.appts ?? []).filter((a) => a.status !== 'cancelled');
  // Đã đến cơ sở / làm dịch vụ (S7, S8…) → coi như xong hết phần Telesales
  const past = !!tagName && !isLeadTag(tagName);
  const talked = touches.some((t) => t.kind === 'call' && isConnected(t.code));
  const tries = touches.filter((t) => t.kind === 'call').length;
  // Theo lịch thật, không theo thẻ: xác nhận cọc có thể đưa khách sang S5 khi chưa có lịch
  const booked = past || appts.length > 0;
  const confirmed = past || appts.some((a) => a.status === 'confirmed' || a.status === 'arrived' || a.status === 'done');
  const appt = [...appts].sort((a, b) => b.at - a.at)[0];
  const quote = (l?.quotes ?? []).filter((q) => q.status !== 'draft' && q.status !== 'cancelled').sort((a, b) => b.at - a.at)[0];
  return [
    { key: 'needs', label: 'Xem nhu cầu', done: !!mang, hint: mang || 'Chưa có mảng' },
    { key: 'call', label: 'Gọi & lưu phiếu', done: past || talked, hint: talked || past ? 'Đã nói chuyện' : tries ? `Gọi ${tries} lần chưa được` : 'Chưa gọi' },
    { key: 'quote', label: 'Báo giá & cọc', optional: true, done: !!quote, hint: quote ? `${quote.code}${quote.deposit ? ' · có cọc' : ''}` : 'Khi khách hỏi giá' },
    { key: 'appt', label: 'Đặt lịch', done: booked, hint: appt ? dayjs(appt.at).format('HH:mm DD/MM') : booked ? 'Đã có lịch' : 'Chưa có lịch' },
    { key: 'confirm', label: 'Xác nhận lịch', done: confirmed, hint: confirmed ? 'Khách đã xác nhận' : booked ? 'Gọi nhắc trước ngày hẹn' : '—' },
  ];
}

/** Một bước do server tính (GET work-steps) */
export interface ServerWorkStep { key: WorkStepKey; done: boolean; hint?: string | null }

/** Gộp bước trên máy với server: một bên xong là xong, server đã xong thì ưu tiên hint của server */
export const mergeSteps = (local: WorkStep[], server?: ServerWorkStep[] | null): WorkStep[] =>
  local.map((s) => {
    const x = server?.find((v) => v.key === s.key);
    return x?.done ? { ...s, done: true, hint: x.hint || s.hint } : s;
  });

/** Khách có thuộc danh sách theo cấu hình list_tag_ids: chưa tải xong (undefined) thì tạm dùng isLeadTag, null = không lọc, chưa có thẻ vẫn hiện */
export const inLeadList = (tag: { id: number; name: string } | null | undefined, ids: number[] | null | undefined) => {
  if (ids === undefined) return isLeadTag(tag?.name);
  return ids === null || !tag || ids.includes(tag.id);
};

/** "Quá 18 phút" / "Còn 9 phút" / "Mai 09:00" */
export const dueText = (due: number, now: number) => {
  if (due < now) return { text: `Quá ${dur(now - due)}`, late: true };
  if (due - now < 6 * HOUR) return { text: `Còn ${dur(due - now)}`, late: false };
  return { text: when(due, now), late: false };
};

export const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/** Telesales đang phụ trách: phân công lại trên máy → contact_staff từ API */
export const staffOf = (r: ChannelCustomerItem, l?: LeadLocal) =>
  l?.assign ? { ...l.assign, local: true } : r.contact_staff ? { id: r.contact_staff.id, name: r.contact_staff.name, local: false } : null;

/** Tình trạng xử lý khách — cho Trực page theo dõi khách mình tạo */
export function progressOf(r: ChannelCustomerItem, l: LeadLocal | undefined, now: number): { text: string; tone: 'ok' | 'warn' | 'late' } {
  const appt = activeAppt(l, now);
  if (appt) return { text: `Đã đặt lịch ${dayjs(appt.at).format('HH:mm DD/MM')}`, tone: 'ok' };
  const visited = l?.appts.find((a) => a.status === 'arrived' || a.status === 'done');
  if (visited) return { text: visited.outcome ? `Đã đến · ${OUTCOMES[visited.outcome.kind].label}` : 'Đã đến cơ sở', tone: 'ok' };
  const noshow = l?.appts.find((a) => a.status === 'noshow');
  const calls = (l?.touches ?? []).filter((t) => t.kind === 'call');
  const lastC = lastCallOf(r, l);
  if (noshow && (!lastC || lastC.at < noshow.at)) return { text: 'Không đến lịch · chờ Telesales gọi lại', tone: 'warn' };
  if (!lastC) {
    const age = now - (r.created_at ? dayjs(r.created_at).valueOf() : now);
    return { text: `Chưa gọi · tạo ${dur(age)} trước`, tone: age > NEW_LEAD_MIN * MIN ? 'late' : 'warn' };
  }
  if (isConnected(lastC.code)) return { text: `Đã liên lạc · ${codeLabel(lastC.code).split(' →')[0]}`, tone: 'ok' };
  return { text: `Đã gọi ${Math.max(calls.length, r.out_call_count ?? 0)} lần · chưa liên lạc được`, tone: 'warn' };
}

/** Thẻ chat (trạng thái chăm sóc gắn ở phòng chat) — quan hệ nhiều-nhiều care_statuses, dự phòng customer_care_status */
export const chatTagsOf = (r: ChannelCustomerItem, l?: LeadLocal): ChatTag[] =>
  l?.chatTags ? l.chatTags : r.care_statuses?.length ? r.care_statuses : r.customer_care_status ? [r.customer_care_status] : [];

/** Màu chữ đọc được trên nền thẻ: nền sáng → chữ tối */
export const textOn = (bg?: string | null) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(bg || '');
  if (!m) return '#fff';
  const n = parseInt(m[1], 16);
  const lum = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return lum > 0.7 ? '#374151' : '#fff';
};
